import {
    Colors,
    Icon,
    slateToHtml,
    type ListItemData,
    type RadioGroupOption,
    htmlToSlate,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { type Descendant } from 'slate';

import type { Evaluation360FormNavigatorParams } from '@/navigation/Evaluation360/Evaluation360FormNavigator';
import type { LaunchReviewTranslationKeys } from '@/pages/shared/ScheduleCreation/components/LaunchReview/LaunchReview';
import type { MultilingualLabel } from '@/types/Locale';

import {
    useGetCycleInstance,
    useLaunchCycleInstance,
} from '@/api/cycle-evaluation-services/cycle-instance';
import { useGetUsersByIds } from '@/api/directory';
import { useSendCycleInstanceTestEmail } from '@/api/email';
import { useUpdateReviewSchedule } from '@/api/review-services/review-schedule';
import { scheduleCreationOnNextAtom } from '@/atoms/evaluation360';
import { userInstanceAtom } from '@/atoms/userDetails';
import { ReviewQuestionStatus } from '@/constants/questionnaire/Question';
import { CycleInstanceStatus } from '@/constants/review/CycleInstance';
import { ReviewScheduleType } from '@/constants/review/ReviewScheduleType';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useError } from '@/hooks/utils/useError';
import { useRegisterPageAction } from '@/hooks/utils/useRegisterPageAction';
import { RouteName } from '@/navigation/types';
import { getRefusedReminderOffsets } from '@/pages/Evaluation360/ScheduleCreation/reminderRefusal';
import { ScheduleCreation } from '@/pages/shared/ScheduleCreation/components/ScheduleCreation';
import {
    ScheduleCreationFormFields,
    scheduleCreationSchema,
    type ScheduleCreationFormData,
} from '@/pages/shared/ScheduleCreation/validation';
import { SuccessMessageAction } from '@/pages/shared/SuccessMessage/SuccessMessagePage';
import i18n from '@/plugins/i18n';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/plugins/Toast';
import { getAllCycleInstanceAssignedUserIds } from '@/utils/cycleInstance';
import {
    getEvaluation360EmailDefaults,
    EVALUATION_360_DEFAULT_TITLES,
    EVALUATION_360_DEFAULT_BODIES,
} from '@/utils/emailDefaults360';
import {
    getSelfEvaluationEmailDefaults,
    SELF_EVALUATION_DEFAULT_TITLES,
    SELF_EVALUATION_DEFAULT_BODIES,
} from '@/utils/emailDefaultsSelfEvaluation';
import {
    getAvailableLanguages,
    normalizeLanguageCode,
    type LocalizedText,
} from '@/utils/localization';
import { getReviewees } from '@/utils/reviewAudience';
import {
    getEffectiveDaysAllowedToComplete,
    getEmptyEmailMessage,
    isScheduleReminderChecked,
    mergeScheduleReminders,
} from '@/utils/schedule';
import { validationMessageKey } from '@/utils/validationMessage';

const ScheduleCreationPage = () => {
    const parseLocalizedField = (
        value: string | MultilingualLabel | null | undefined,
    ): LocalizedText => {
        if (value === null || value === undefined || typeof value === 'string') {
            return {};
        }
        return value as unknown as LocalizedText;
    };

    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const navigate = useNavigate();
    const { cycleInstanceId } = useParams<Evaluation360FormNavigatorParams>();

    const {
        data: cycleInstance,
        isLoading: isCycleInstanceLoading,
        isError: isCycleInstanceError,
    } = useGetCycleInstance(cycleInstanceId, {
        includeInactiveQuestions: true,
    });

    const userIds: string[] = useMemo(() => {
        if (!cycleInstance) {
            return [];
        }

        return Array.from(getAllCycleInstanceAssignedUserIds([cycleInstance]));
    }, [cycleInstance]);

    const hasSelfAssessment = cycleInstance?.reviewType === 'SelfEvaluation';

    const getEmailDefaults = useCallback(
        (lang: string, reviewType: string | undefined) =>
            reviewType === 'SelfEvaluation'
                ? getSelfEvaluationEmailDefaults(lang)
                : getEvaluation360EmailDefaults(lang),
        [],
    );

    const isDefaultTitle = useCallback(
        (value: string, reviewType: string | undefined) =>
            EVALUATION_360_DEFAULT_TITLES.has(value) ||
            SELF_EVALUATION_DEFAULT_TITLES.has(value) ||
            (reviewType === 'Evaluation360' && SELF_EVALUATION_DEFAULT_TITLES.has(value)) ||
            (reviewType === 'SelfEvaluation' && EVALUATION_360_DEFAULT_TITLES.has(value)),
        [],
    );

    const isDefaultBody = useCallback((value: string, reviewType: string | undefined) => {
        if (reviewType === 'SelfEvaluation' && value.includes('{{RevieweeName}}')) {
            return true;
        }
        return (
            EVALUATION_360_DEFAULT_BODIES.has(value) || SELF_EVALUATION_DEFAULT_BODIES.has(value)
        );
    }, []);

    const { data: users, isLoading: isUsersLoading } = useGetUsersByIds({
        userIds,
        identifier: 'schedule-creation',
    });

    const { mutateAsync: updateSchedule, isPending: isUpdatingSchedule } =
        useUpdateReviewSchedule();
    const { mutateAsync: launchEvaluation, isPending: isLaunching } = useLaunchCycleInstance();
    const { mutateAsync: sendTestEmail, isPending: isSendingTestEmail } =
        useSendCycleInstanceTestEmail();

    const { watch, setValue, reset, trigger, getFieldState } = useForm<ScheduleCreationFormData>({
        resolver: zodResolver(scheduleCreationSchema),
        defaultValues: {
            [ScheduleCreationFormFields.daysToComplete]: 14,
            [ScheduleCreationFormFields.scheduleType]: ReviewScheduleType.SEND_IMMEDIATELY,
            [ScheduleCreationFormFields.scheduledAt]: undefined,
            [ScheduleCreationFormFields.reminderSevenDays]: false,
            [ScheduleCreationFormFields.reminderThreeDays]: false,
            [ScheduleCreationFormFields.reminderOnDeadline]: false,
            [ScheduleCreationFormFields.managerViewScores]: true,
            [ScheduleCreationFormFields.managerReadComments]: true,
            [ScheduleCreationFormFields.managerAccessIndividual]: true,
            [ScheduleCreationFormFields.emailLanguage]: normalizeLanguageCode(i18n.language),
            [ScheduleCreationFormFields.emailTitle]: '',
            [ScheduleCreationFormFields.emailMessage]: getEmptyEmailMessage(),
        },
    });

    const formValues = watch();
    const availableLanguageCodes = useMemo(() => Object.keys(getAvailableLanguages()), []);
    const [emailTitleByLanguage, setEmailTitleByLanguage] = useState<LocalizedText>({});
    const [emailMessageByLanguage, setEmailMessageByLanguage] = useState<
        Record<string, Descendant[]>
    >({});
    const [customizedLanguages, setCustomizedLanguages] = useState<Set<string>>(new Set());

    const currentUser = useAtomValue(userInstanceAtom);
    const [testEmailAddress, setTestEmailAddress] = useState<string>('');
    const [isFormInitialized, setIsFormInitialized] = useState(false);
    const [hasUserEditedScheduledAt, setHasUserEditedScheduledAt] = useState(false);

    useEffect(() => {
        if (currentUser.email) {
            setTestEmailAddress(currentUser.email);
        }
    }, [currentUser.email]);

    useEffect(() => {
        if (cycleInstance?.reviewSchedule && cycleInstance?.reviewType !== undefined) {
            const reviewType = cycleInstance.reviewType;
            const schedule = cycleInstance.reviewSchedule;
            const emailLanguage = normalizeLanguageCode(i18n.language);
            const currentPayloadLanguage = emailLanguage;

            const localizedEmailTitles = parseLocalizedField(schedule.emailTitle);
            const localizedEmailMessages = parseLocalizedField(schedule.emailMessage);
            const customized = new Set<string>();

            const storedTitle =
                typeof schedule.emailTitle === 'string' ? schedule.emailTitle : null;
            const storedMessage =
                typeof schedule.emailMessage === 'string' ? schedule.emailMessage : null;

            const emailTitleByLanguageValue: LocalizedText =
                storedTitle !== null
                    ? {
                          [currentPayloadLanguage]:
                              storedTitle && !isDefaultTitle(storedTitle, reviewType)
                                  ? storedTitle
                                  : getEmailDefaults(currentPayloadLanguage, reviewType).title,
                      }
                    : Object.fromEntries(
                          Object.keys(localizedEmailTitles).map((lang) => {
                              const stored = localizedEmailTitles[lang];
                              const defaultTitle = getEmailDefaults(lang, reviewType).title;
                              const storedMsg = localizedEmailMessages[lang];
                              const defaultBody = getEmailDefaults(lang, reviewType).body;
                              const isTitleCustomized =
                                  stored &&
                                  stored !== defaultTitle &&
                                  !isDefaultTitle(stored, reviewType);
                              const isBodyCustomized =
                                  storedMsg &&
                                  storedMsg !== defaultBody &&
                                  !isDefaultBody(storedMsg, reviewType);
                              if (isTitleCustomized || isBodyCustomized) {
                                  customized.add(lang);
                              }
                              return [lang, isTitleCustomized ? stored : defaultTitle];
                          }),
                      );
            const emailMessageByLanguageValue: Record<string, Descendant[]> =
                storedMessage !== null
                    ? {
                          [currentPayloadLanguage]: htmlToSlate(
                              storedMessage && !isDefaultBody(storedMessage, reviewType)
                                  ? storedMessage
                                  : getEmailDefaults(currentPayloadLanguage, reviewType).body,
                          ),
                      }
                    : Object.entries(localizedEmailMessages).reduce(
                          (acc, [key, value]) => ({
                              ...acc,
                              [key]: htmlToSlate(
                                  value && !isDefaultBody(value, reviewType)
                                      ? value
                                      : getEmailDefaults(key, reviewType).body,
                              ),
                          }),
                          {} as Record<string, Descendant[]>,
                      );

            setCustomizedLanguages(customized);

            const allLanguagesTitleDefaults = availableLanguageCodes.reduce(
                (acc, lang) => ({
                    ...acc,
                    [lang]:
                        emailTitleByLanguageValue[lang] || getEmailDefaults(lang, reviewType).title,
                }),
                {} as LocalizedText,
            );
            const allLanguagesMessageDefaults = availableLanguageCodes.reduce(
                (acc, lang) => ({
                    ...acc,
                    [lang]:
                        emailMessageByLanguageValue[lang] ??
                        htmlToSlate(getEmailDefaults(lang, reviewType).body),
                }),
                {} as Record<string, Descendant[]>,
            );

            setEmailTitleByLanguage(allLanguagesTitleDefaults);
            setEmailMessageByLanguage(allLanguagesMessageDefaults);

            reset({
                [ScheduleCreationFormFields.daysToComplete]: schedule.daysAllowedToComplete,
                [ScheduleCreationFormFields.scheduleType]: schedule.scheduleType,
                [ScheduleCreationFormFields.scheduledAt]: new Date(schedule.scheduledAt),
                [ScheduleCreationFormFields.reminderSevenDays]: isScheduleReminderChecked({
                    scheduleReminder: schedule.scheduleReminders,
                    daysBeforeDeadline: 7,
                }),
                [ScheduleCreationFormFields.reminderThreeDays]: isScheduleReminderChecked({
                    scheduleReminder: schedule.scheduleReminders,
                    daysBeforeDeadline: 3,
                }),
                [ScheduleCreationFormFields.reminderOnDeadline]: isScheduleReminderChecked({
                    scheduleReminder: schedule.scheduleReminders,
                    daysBeforeDeadline: 0,
                }),
                [ScheduleCreationFormFields.managerViewScores]: schedule.showScoresToTheReviewee,
                [ScheduleCreationFormFields.managerReadComments]:
                    schedule.showCommentsToTheReviewee,
                [ScheduleCreationFormFields.emailLanguage]: emailLanguage,
                [ScheduleCreationFormFields.emailTitle]:
                    emailTitleByLanguageValue[currentPayloadLanguage] ??
                    getEmailDefaults(currentPayloadLanguage, reviewType).title,
                [ScheduleCreationFormFields.emailMessage]:
                    emailMessageByLanguageValue[currentPayloadLanguage] ??
                    htmlToSlate(getEmailDefaults(currentPayloadLanguage, reviewType).body),
            });
            setIsFormInitialized(true);
        }
    }, [
        cycleInstance,
        cycleInstance?.reviewType,
        reset,
        getEmailDefaults,
        isDefaultTitle,
        isDefaultBody,
        availableLanguageCodes,
    ]);

    // Deliberately scoped to cycleInstanceId only: also depending on cycleInstance would reset
    // this flag on every Save and let an already-saved past date slip back through as untouched.
    useEffect(() => {
        setHasUserEditedScheduledAt(false);
    }, [cycleInstanceId]);

    const reviewees = useMemo(
        () =>
            getReviewees({
                reviewAudience: cycleInstance?.reviewAudience,
                users,
            }) || [],
        [cycleInstance, users],
    );

    const options: RadioGroupOption[] = useMemo(
        () => [
            {
                label: t('evaluation360ScheduleCreation.scheduleOptions.sendImmediately'),
                value: ReviewScheduleType.SEND_IMMEDIATELY,
            },
            {
                label: t('evaluation360ScheduleCreation.scheduleOptions.sendLater'),
                value: ReviewScheduleType.SCHEDULE_FOR_LATER,
            },
        ],
        [t],
    );

    const managerAccessList: ListItemData[] = useMemo(
        () => [
            {
                content: t('evaluation360ScheduleCreation.resultsVisibility.viewAllScore'),
                icon: (
                    <Icon
                        name={'essential-checkmark'}
                        color={Colors.PRIMARY}
                    />
                ),
            },
            {
                content: t('evaluation360ScheduleCreation.resultsVisibility.readAllComments'),
                icon: (
                    <Icon
                        name={'essential-checkmark'}
                        color={Colors.PRIMARY}
                    />
                ),
            },
            {
                content: t('evaluation360ScheduleCreation.resultsVisibility.accessIndividual'),
                icon: (
                    <Icon
                        name={'essential-checkmark'}
                        color={Colors.PRIMARY}
                    />
                ),
            },
        ],
        [t],
    );

    const onSchedulingOptionChange = useCallback(
        (value: string) => {
            setValue(ScheduleCreationFormFields.scheduleType, value as ReviewScheduleType);
        },
        [setValue],
    );

    const onDaysToCompleteChange = useCallback(
        (value: number) => {
            setValue(ScheduleCreationFormFields.daysToComplete, value);
        },
        [setValue],
    );

    const onReminderChange = useCallback(
        ({ field, value }: { field: keyof typeof ScheduleCreationFormFields; value: boolean }) => {
            setValue(field as keyof ScheduleCreationFormData, value);
        },
        [setValue],
    );

    const onEmailLanguageChange = useCallback(
        (value: string) => {
            const currentPayloadLanguage = formValues.emailLanguage;
            const selectedPayloadLanguage = value;

            setEmailTitleByLanguage((previous) => ({
                ...previous,
                [currentPayloadLanguage]: formValues.emailTitle,
            }));
            setEmailMessageByLanguage((previous) => ({
                ...previous,
                [currentPayloadLanguage]: formValues.emailMessage,
            }));

            setValue(ScheduleCreationFormFields.emailLanguage, value);
            setValue(
                ScheduleCreationFormFields.emailTitle,
                emailTitleByLanguage[selectedPayloadLanguage] ??
                    getEmailDefaults(selectedPayloadLanguage, cycleInstance?.reviewType).title,
            );
            setValue(
                ScheduleCreationFormFields.emailMessage,
                emailMessageByLanguage[selectedPayloadLanguage] ??
                    htmlToSlate(
                        getEmailDefaults(selectedPayloadLanguage, cycleInstance?.reviewType).body,
                    ),
                { shouldValidate: true },
            );
        },
        [
            formValues,
            setValue,
            emailTitleByLanguage,
            emailMessageByLanguage,
            getEmailDefaults,
            cycleInstance?.reviewType,
        ],
    );

    const onEmailTitleChange = useCallback(
        (value: string) => {
            setValue(ScheduleCreationFormFields.emailTitle, value);
            const currentPayloadLanguage = formValues.emailLanguage;
            setEmailTitleByLanguage((previous) => ({
                ...previous,
                [currentPayloadLanguage]: value,
            }));
            setCustomizedLanguages((previous) => new Set(previous).add(currentPayloadLanguage));
        },
        [setValue, formValues.emailLanguage],
    );

    const onEmailMessageChange = useCallback(
        (value: Descendant[]) => {
            setValue(ScheduleCreationFormFields.emailMessage, value, { shouldValidate: true });
            const currentPayloadLanguage = formValues.emailLanguage;
            setEmailMessageByLanguage((previous) => ({
                ...previous,
                [currentPayloadLanguage]: value,
            }));
            setCustomizedLanguages((previous) => new Set(previous).add(currentPayloadLanguage));
        },
        [setValue, formValues.emailLanguage],
    );

    const onScheduledAtChange = (date?: Date) => {
        const newDate = date ?? new Date();
        setValue(ScheduleCreationFormFields.scheduledAt, newDate);
        setHasUserEditedScheduledAt(true);
    };

    const validateScheduleForm = useCallback(async (): Promise<boolean> => {
        const isFormValid = await trigger([
            ScheduleCreationFormFields.daysToComplete,
            ScheduleCreationFormFields.emailTitle,
            ScheduleCreationFormFields.emailMessage,
        ]);

        if (!isFormValid) {
            const daysState = getFieldState(ScheduleCreationFormFields.daysToComplete);
            const titleState = getFieldState(ScheduleCreationFormFields.emailTitle);
            const messageState = getFieldState(ScheduleCreationFormFields.emailMessage);

            const errorMessage =
                validationMessageKey(daysState.error?.message) ||
                validationMessageKey(titleState.error?.message) ||
                validationMessageKey(messageState.error?.message);

            showWarningToast({
                title: t('errors.genericTitle'),
                message: errorMessage ? t(errorMessage) : '',
            });

            return false;
        }

        return true;
    }, [trigger, getFieldState, t]);

    const isScheduledAtInPastByMinute = useCallback((scheduledAt: Date): boolean => {
        const scheduledAtByMinute = new Date(scheduledAt);
        const nowByMinute = new Date();

        scheduledAtByMinute.setSeconds(0, 0);
        nowByMinute.setSeconds(0, 0);

        return scheduledAtByMinute < nowByMinute;
    }, []);

    const scheduleReminders = mergeScheduleReminders({
        storedReminders: cycleInstance?.reviewSchedule?.scheduleReminders,
        reminderSevenDays: formValues.reminderSevenDays,
        reminderThreeDays: formValues.reminderThreeDays,
        reminderOnDeadline: formValues.reminderOnDeadline,
    });

    /**
     * Closing an option, explaining its refusal, and the payload that gets saved all have to name
     * the same window, so all three read this single expression instead of recomputing their own.
     */
    const completionWindowDays = getEffectiveDaysAllowedToComplete({
        daysToComplete: formValues.daysToComplete,
        storedDaysAllowedToComplete: cycleInstance?.reviewSchedule?.daysAllowedToComplete,
    });

    const handleScheduleError = useCallback(
        (error: unknown) => {
            const refusedOffsets = getRefusedReminderOffsets({
                error,
                scheduleReminders,
                daysAllowedToComplete: completionWindowDays,
            });

            if (refusedOffsets.length === 0) {
                handleError({ error });

                return;
            }

            const refusedDelays = refusedOffsets
                .map((days) =>
                    t('evaluation360ScheduleCreation.automatedReminders.delayOfDays', { days }),
                )
                .join(', ');

            handleError({
                error,
                data: {
                    title: t('evaluation360ScheduleCreation.automatedReminders.outsideWindowTitle'),
                    message: t('evaluation360ScheduleCreation.automatedReminders.outsideWindow', {
                        delays: refusedDelays,
                        window: completionWindowDays,
                    }),
                },
            });
        },
        [scheduleReminders, completionWindowDays, handleError, t],
    );

    const saveSchedule = useCallback(
        async (overrideScheduledAt?: Date) => {
            if (!cycleInstance?.reviewSchedule) {
                return;
            }

            const scheduledAt: string =
                formValues.scheduleType === ReviewScheduleType.SCHEDULE_FOR_LATER
                    ? (overrideScheduledAt ?? formValues.scheduledAt).toISOString()
                    : new Date().toISOString();

            const currentPayloadLanguage = formValues.emailLanguage;
            const allCustomized = new Set(customizedLanguages).add(currentPayloadLanguage);
            const mergedTitles = {
                ...emailTitleByLanguage,
                [currentPayloadLanguage]: formValues.emailTitle,
            };
            const mergedMessages = {
                ...emailMessageByLanguage,
                [currentPayloadLanguage]: formValues.emailMessage,
            };

            const customEmailTitle = Object.fromEntries(
                [...allCustomized]
                    .filter((lang) => mergedTitles[lang] !== undefined)
                    .map((lang) => [lang, mergedTitles[lang]]),
            );
            const customEmailMessage = Object.fromEntries(
                [...allCustomized]
                    .filter((lang) => mergedMessages[lang] !== undefined)
                    .map((lang) => [lang, slateToHtml(mergedMessages[lang])]),
            );

            await updateSchedule({
                ...cycleInstance.reviewSchedule,
                daysAllowedToComplete:
                    completionWindowDays ?? cycleInstance.reviewSchedule.daysAllowedToComplete,
                scheduleType: formValues.scheduleType ?? cycleInstance.reviewSchedule.scheduleType,
                scheduledAt,
                showScoresToTheReviewee: formValues.managerViewScores,
                showCommentsToTheReviewee: formValues.managerReadComments,
                emailTitle: customEmailTitle as unknown as MultilingualLabel,
                emailMessage: customEmailMessage as unknown as MultilingualLabel,
                scheduleReminders,
            });
        },
        [
            updateSchedule,
            cycleInstance,
            formValues,
            completionWindowDays,
            scheduleReminders,
            emailTitleByLanguage,
            emailMessageByLanguage,
            customizedLanguages,
        ],
    );

    const onSaveSettings = useCallback(async () => {
        const isValid = await validateScheduleForm();
        if (!isValid) {
            return;
        }

        try {
            await saveSchedule();
            showSuccessToast({
                title: t('evaluation360ScheduleCreation.saveSettingsSuccessTitle'),
                message: t('evaluation360ScheduleCreation.saveSettingsSuccessMessage'),
            });
        } catch (error) {
            handleScheduleError(error);
        }
    }, [validateScheduleForm, saveSchedule, t, handleScheduleError]);

    const onTestEmail = useCallback(async () => {
        const isTitleValid = await trigger(ScheduleCreationFormFields.emailTitle);
        const isMessageValid = await trigger(ScheduleCreationFormFields.emailMessage);

        if (!isTitleValid || !isMessageValid) {
            const titleState = getFieldState(ScheduleCreationFormFields.emailTitle);
            const messageState = getFieldState(ScheduleCreationFormFields.emailMessage);

            const titleErrorMessage = validationMessageKey(titleState.error?.message);
            const messageErrorMessage = validationMessageKey(messageState.error?.message);
            const errorMessage = titleErrorMessage || messageErrorMessage;

            showWarningToast({
                title: t('errors.genericTitle'),
                message: errorMessage ? t(errorMessage) : '',
            });
            return;
        }

        if (!cycleInstance) {
            handleError({});

            return false;
        }

        try {
            await sendTestEmail({
                emailTitle: formValues.emailTitle,
                emailMessage: slateToHtml(formValues.emailMessage),
                cycleInstanceId: cycleInstance?.id,
                emailAddress: testEmailAddress,
                selectedLang: formValues.emailLanguage,
            });

            showSuccessToast({
                title: t('evaluation360ScheduleCreation.testEmail.successTitle'),
                message: t('evaluation360ScheduleCreation.testEmail.successMessage'),
            });
        } catch (error) {
            handleError({ error });
        }
    }, [
        trigger,
        getFieldState,
        t,
        sendTestEmail,
        cycleInstance,
        formValues.emailTitle,
        formValues.emailMessage,
        testEmailAddress,
        handleError,
    ]);

    const onLaunch = useCallback(async () => {
        const isLaunched = cycleInstance?.status === CycleInstanceStatus.LAUNCHED;
        const isScheduledForLater =
            !isLaunched && formValues.scheduleType === ReviewScheduleType.SCHEDULE_FOR_LATER;
        const isScheduledAtStale =
            isScheduledForLater &&
            !!formValues.scheduledAt &&
            isScheduledAtInPastByMinute(formValues.scheduledAt);

        if (isScheduledAtStale && hasUserEditedScheduledAt) {
            showErrorToast({
                title: t('cycleEvaluationSchedule.validation.scheduledAtPastTitle'),
                message: t('cycleEvaluationSchedule.validation.scheduledAtPast'),
                action: {
                    label: t('cycleEvaluationSchedule.validation.resetToTodayAction'),
                    onClick: () => setValue(ScheduleCreationFormFields.scheduledAt, new Date()),
                },
            });
            return false;
        }

        const effectiveScheduledAt = isScheduledAtStale ? new Date() : formValues.scheduledAt;

        if (isScheduledAtStale) {
            setValue(ScheduleCreationFormFields.scheduledAt, effectiveScheduledAt as Date);
        }

        const isValid = await validateScheduleForm();

        if (!isValid) {
            return false;
        }

        if (!cycleInstance) {
            handleError({});

            return false;
        }
        const activeQuestions =
            cycleInstance.reviewQuestionnaire.reviewQuestions?.filter(
                (question) => question.status === ReviewQuestionStatus.ACTIVE,
            ) || [];
        if (!activeQuestions?.length) {
            showWarningToast({
                message: t('evaluation360Questionnaire.message.noQuestions'),
            });
            return false;
        }

        try {
            await saveSchedule(effectiveScheduledAt);
            await launchEvaluation(cycleInstance.id);

            showSuccessToast({
                title: t('evaluation360ScheduleCreation.saveSettingsSuccessTitle'),
                message: t('evaluation360ScheduleCreation.saveSettingsSuccessMessage'),
            });

            navigate({
                path: RouteName.SuccessMessage,
                state: {
                    title: hasSelfAssessment
                        ? t('selfAssessmentSuccessMessage.title')
                        : t('evaluation360SuccessMessage.title'),
                    buttonLabel: hasSelfAssessment
                        ? t('selfAssessmentSuccessMessage.goToDashboard')
                        : t('evaluation360SuccessMessage.goToDashboard'),
                    actionType: SuccessMessageAction.NAVIGATE_TO_EVAL_DASHBOARD,
                },
            });
            return true;
        } catch (error) {
            handleScheduleError(error);
            return false;
        }
    }, [
        validateScheduleForm,
        launchEvaluation,
        cycleInstance,
        formValues,
        navigate,
        handleError,
        handleScheduleError,
        saveSchedule,
        setValue,
        hasUserEditedScheduledAt,
        isScheduledAtInPastByMinute,
        t,
    ]);

    useFetchErrorsHandler({
        errors: [isCycleInstanceError],
    });

    useRegisterPageAction(scheduleCreationOnNextAtom, onLaunch);

    const isAlreadyLaunched = cycleInstance?.status === CycleInstanceStatus.LAUNCHED;

    const launchTranslationKeys = useMemo<LaunchReviewTranslationKeys>(
        () =>
            isAlreadyLaunched
                ? {
                      titleKey: 'evaluation360ScheduleCreation.updateReview.title',
                      ctaKey: 'evaluation360ScheduleCreation.updateReview.launch',
                  }
                : {
                      titleKey: 'evaluation360ScheduleCreation.launchReview.title',
                      ctaKey: 'evaluation360ScheduleCreation.launchReview.launch',
                  },
        [isAlreadyLaunched],
    );

    const title = hasSelfAssessment
        ? t('evaluation360ScheduleCreation.header.selfAssessmentPerformanceReviewSetup')
        : t('evaluation360ScheduleCreation.header.performanceReviewSetup');

    return (
        <ScheduleCreation
            title={title}
            subtitle={t('evaluation360ScheduleCreation.header.configureAndLaunch')}
            reviewees={reviewees}
            options={options}
            managerAccessList={managerAccessList}
            formValues={formValues}
            completionWindowDays={completionWindowDays}
            isLoading={
                !!cycleInstance?.id &&
                (!isFormInitialized || isUsersLoading || isCycleInstanceLoading)
            }
            isSendingTestEmail={isSendingTestEmail}
            isSavingSchedule={isUpdatingSchedule}
            isLaunching={isLaunching}
            onSchedulingOptionChange={onSchedulingOptionChange}
            onDaysToCompleteChange={onDaysToCompleteChange}
            onReminderChange={onReminderChange}
            testEmailAddress={testEmailAddress}
            onEmailLanguageChange={onEmailLanguageChange}
            onEmailTitleChange={onEmailTitleChange}
            onEmailMessageChange={onEmailMessageChange}
            onTestEmailAddressChange={setTestEmailAddress}
            editorKey={`schedule-email-editor-${formValues.emailLanguage}`}
            onTestEmail={onTestEmail}
            onSaveSettings={onSaveSettings}
            onLaunchReview={onLaunch}
            onScheduledAtChange={onScheduledAtChange}
            scheduledAt={formValues.scheduledAt}
            hasSelfAssessment={hasSelfAssessment}
            launchTranslationKeys={launchTranslationKeys}
        />
    );
};

export { ScheduleCreationPage };
